import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../../services/authService';
import { purchaseService } from '../../services/purchaseService';
import {
  setCredentials,
  setError,
  setStatus,
  selectAuthStatus,
  selectAuthError,
} from '../../redux/slices/authSlice';
import { setPurchasedFromApi } from '../../redux/slices/ebookAccessSlice';
import {
  PageContainer,
  Card,
  Title,
  Subtitle,
  Field,
  SubmitButton,
  SwitchLink,
  ErrorBox,
  SuccessBox,
  ForgotPasswordLink,
  Modal,
  ModalContent,
  CloseButton,
} from './auth.styles';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const status = useSelector(selectAuthStatus);
  const error = useSelector(selectAuthError);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStatus, setForgotStatus] = useState('idle');
  const [forgotMessage, setForgotMessage] = useState('');

  const isSubmitting = status === 'loading';
  const isForgotSubmitting = forgotStatus === 'loading';
  const redirectTo = location.state?.from || '/minhas-compras';

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(setStatus('loading'));
    try {
      const { token, user } = await authService.login({ email, password });
      console.log('✅ [Login] Login bem-sucedido');
      
      dispatch(setCredentials({ token, user }));

      // ===== Sincronizar compras órfãs (navegadores diferentes) =====
      console.log('🔄 [Login] Tentando reivindicar compras órfãs com email:', email);
      try {
        const orphanResult = await purchaseService.claimOrphanPurchases(token, email);
        console.log('📦 [Login] Compras órfãs reivindicadas:', orphanResult);
        if (orphanResult.claimed > 0) {
          console.log(`✅ [Login] ${orphanResult.claimed} compra(s) órfã(s) associada(s)`);
          // Atualizar Redux com as compras reivindicadas
          const allPurchases = await purchaseService.listMine(token);
          dispatch(setPurchasedFromApi(allPurchases.purchases || []));
        }
      } catch (orphanErr) {
        console.warn('⚠️ [Login] Erro ao reivindicar compras órfãs (não é crítico):', orphanErr.message);
      }

      // ===== Sincronizar compras anônimas (mesmo navegador) =====
      console.log('📝 [Login] Sincronizando compras anônimas do localStorage...');
      try {
        const syncResult = await purchaseService.syncAnonymousPurchasesToAccount(token, email);
        console.log('📦 [Login] Resultado da sincronização:', syncResult);

        // Carregar todas as compras (anônimas sincronizadas + reivindicadas + anteriores)
        const purchasesData = await purchaseService.listMine(token);
        console.log('✅ [Login] Compras carregadas após sincronização:', purchasesData);
        dispatch(setPurchasedFromApi(purchasesData.purchases || []));
      } catch (syncErr) {
        console.error('⚠️ [Login] Erro ao sincronizar compras anônimas (não é crítico):', syncErr);
        // Mesmo se falhar, continua
      }

      navigate(redirectTo, { replace: true });
    } catch (err) {
      dispatch(setError(err.message || 'Falha ao entrar'));
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotStatus('loading');
    setForgotMessage('');
    try {
      await authService.forgotPassword(forgotEmail);
      // Salvar email no localStorage para usar na página de reset
      localStorage.setItem('resetEmail', forgotEmail);
      setForgotStatus('success');
      setForgotMessage('Verifique seu e-mail. Você receberá um código de 6 dígitos para resetar sua senha');
      setTimeout(() => {
        setShowForgotModal(false);
        setForgotEmail('');
        setForgotStatus('idle');
        setForgotMessage('');
        navigate('/reset-senha');
      }, 1500);
    } catch (err) {
      setForgotStatus('error');
      setForgotMessage(err.message || 'Falha ao enviar e-mail');
    }
  };

  return (
    <PageContainer>
      <Card onSubmit={handleSubmit} noValidate>
        <Title>Entrar</Title>
        <Subtitle>Acesse seus eBooks e cursos</Subtitle>

        {error && <ErrorBox>{error}</ErrorBox>}

        <Field>
          E-mail
          <input
            type="email"
            autoComplete="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isSubmitting}
          />
        </Field>

        <Field>
          Senha
          <input
            type="password"
            autoComplete="current-password"
            placeholder="Sua senha"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            disabled={isSubmitting}
          />
        </Field>

        <ForgotPasswordLink
          type="button"
          onClick={() => setShowForgotModal(true)}
        >
          Esqueci minha senha
        </ForgotPasswordLink>

        <SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Entrando…' : 'Entrar'}
        </SubmitButton>

        <SwitchLink>
          Ainda não tem conta? <Link to="/cadastro">Cadastre-se</Link>
        </SwitchLink>
      </Card>

      {/* Modal de Esqueci Senha */}
      <Modal $isOpen={showForgotModal} onClick={() => setShowForgotModal(false)}>
        <ModalContent 
          $isOpen={showForgotModal}
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton 
            onClick={() => setShowForgotModal(false)}
            aria-label="Fechar"
          >
            ✕
          </CloseButton>

          <Title style={{ marginTop: 0 }}>Recuperar Senha</Title>
          <Subtitle>Digite seu e-mail para receber um código de recuperação</Subtitle>

          <form onSubmit={handleForgotPassword} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {forgotMessage && (
              forgotStatus === 'success' ? (
                <SuccessBox>{forgotMessage}</SuccessBox>
              ) : (
                <ErrorBox>{forgotMessage}</ErrorBox>
              )
            )}

            <Field>
              E-mail
              <input
                type="email"
                autoComplete="email"
                placeholder="seu@email.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                disabled={isForgotSubmitting}
              />
            </Field>

            <SubmitButton type="submit" disabled={isForgotSubmitting}>
              {isForgotSubmitting ? 'Enviando…' : 'Enviar Código'}
            </SubmitButton>
          </form>
        </ModalContent>
      </Modal>
    </PageContainer>
  );
};

export default Login;
